import { toast } from "sonner";
import customFetch from "../util/config/axiosConfig";
import { getLocalStorage } from "../util/manageStorage";
import { QueryItem, QueryListResponse } from "../types/Query";
import { ApiResponse } from "../types/responseType";

const QueryApis = {
  // post
  createQuery: async (
    payload: QueryItem,
  ): Promise<ApiResponse<QueryItem> | undefined> => {
    try {
      const response = await customFetch.post(`/api/v1/query`, payload);
      toast.success("Query added successfully!");
      return response.data as ApiResponse<QueryItem>;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Something went wrong");
      return undefined;
    }
  },

  // get all
  getQueries: async (
    page = 0,
    size = 50,
    branchCode: string,
  ): Promise<ApiResponse<QueryListResponse> | undefined> => {
    try {
      if (!branchCode) throw new Error("Branch code is required");

      const trimmedBranchCode = branchCode.trim();

      const response = await customFetch.get(
        `/api/v1/query?branchCode=${trimmedBranchCode}&page=${page}&size=${size}`,
      );
      return response.data as ApiResponse<QueryListResponse>;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load queries",
      );
      return undefined;
    }
  },

  // update
  updateQuery: async (
    id: number,
    payload: QueryItem,
  ): Promise<ApiResponse<QueryItem> | undefined> => {
    try {
      const response = await customFetch.put(`/api/v1/query/${id}`, payload);
      toast.success("Query updated successfully");
      return response.data as ApiResponse<QueryItem>;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update query");
      return undefined;
    }
  },

  // get by id
  getQueryById: async (
    id: number,
  ): Promise<ApiResponse<QueryItem> | undefined> => {
    try {
      const token = getLocalStorage("user")?.accessToken;
      const response = await customFetch.get(`/api/v1/query/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data as ApiResponse<QueryItem>;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load query details",
      );
      return undefined;
    }
  },

  // delete by id
  deleteQuery: async (id: number): Promise<ApiResponse<null> | undefined> => {
    try {
      const token = getLocalStorage("user")?.accessToken;
      const response = await customFetch.delete(`/api/v1/query?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Query deleted successfully");
      return response.data as ApiResponse<null>;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete query");
      return undefined;
    }
  },

  // status
  queriesStatus: async (): Promise<ApiResponse<string[]> | undefined> => {
    try {
      const response = await customFetch.get(`/api/v1/query/all-status`);

      return response.data as ApiResponse<string[]>;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Something went wrong");
      return undefined;
    }
  },

  // move query
  moveQueryToStatus: async (
    id: number,
    status: string,
    visitScheduledOn?: string,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.get(`/api/v1/query/move-to/${id}`, {
        params: { status, visitScheduledOn },
      });

      toast.success(response.data?.data || "Status updated successfully");
      return response.data as ApiResponse<string>;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to update query status",
      );
      return undefined;
    }
  },

  // assign query to
  assignQuery: async (
    userId: number,
    queryId: number,
  ): Promise<ApiResponse<QueryItem> | undefined> => {
    try {
      const response = await customFetch.get(
        `/api/v1/query/assign-query/${userId}/${queryId}`,
      );

      toast.success("Query assigned successfully");
      return response.data as ApiResponse<QueryItem>;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to assign query");
      return undefined;
    }
  },
};

export default QueryApis;
